<template>
  <div class="w-full">
    <div class="w-full max-w-[450px] mx-auto">
      <div class="font-secondary text-[26px] leading-[36px] text-gray-800 mb-4">
        Your journey to protecting your work
        <span class="text-green-700 font-semibold">starts here.</span>
      </div>

      <div
        class="flex flex-col gap-y-2.5 font-secondary text-[14px] leading-[20px] mb-[30px]"
      >
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div>Copyright protect all your work</div>
        </div>
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div>Invite and control who can access your copyrights</div>
        </div>
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div>
            <span
              class="w-fit bg-linear-to-r from-yellow-500 via-[#F84600] to-[#CE489A] bg-clip-text text-transparent font-medium"
            >
              Dacr AI
            </span>
            to discover and fight piracy
          </div>
        </div>
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div>Add unlimited co-owners and authors</div>
        </div>
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div>License and sell your work + collect royalties</div>
        </div>
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div>Manage copyrights on behalf of others</div>
        </div>
      </div>

      <div class="flex flex-col gap-y-2.5 mb-5">
        <template v-if="monthlyPlans">
          <PlanOption
            v-for="plan in monthlyPlans"
            v-model="selectedPlan"
            :key="plan.id"
            :plan="plan"
          />
        </template>

        <Skeleton
          v-else
          v-for="n in 2"
          :key="n"
          class="w-full h-[108px] rounded-xl"
        />
      </div>

      <UiButton
        class="w-full"
        :disabled="!selectedPlan"
        primary
        large
        @click="handleClick"
      >
        Continue
      </UiButton>

      <div
        v-if="props.type === SelectPlanType.Onboarding"
        class="mt-5 font-secondary text-gray-800 text-[12px] leading-[20px] text-center font-medium cursor-pointer"
        @click="handleFreeClick"
      >
        Try Dacr for free
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  PlanItemStatus,
  PlanPriceType,
  useCompleteOnboardingMutation,
  usePlansQueryQuery,
  type Plan,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import { useUserStore } from '@/shared/stores/user'
import { getTokens } from '@dacr/auth'
import { Icon, Skeleton, UiButton } from '@dacr/ui'
import { computed, ref, watch } from 'vue'
import PlanOption from './PlanOption.vue'
import { SelectPlanType } from '@/shared/types/index'
import { useRouter } from 'vue-router'
import { useDevice } from '@/shared/composables/useDevice'
import { analytics } from '@/shared/services/analytics'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'

const props = defineProps<{
  type: SelectPlanType
}>()

const device = useDevice()
const router = useRouter()
const dialog = useDialog()
const billingStore = useBillingStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const { result } = usePlansQueryQuery(
  { accessToken: getTokens().accessToken },
  { enabled: !webviewStore.isActive }
)

const { mutate: completeOnboarding } = useCompleteOnboardingMutation()

const plans = computed(() => result.value?.plans || billingStore.webviewPlans)

const selectedPlan = ref<Plan>()

const monthlyPlans = computed(
  () =>
    plans.value?.groupByBillingCycle.find(
      (group) => group.billingCycleType === 'month'
    )?.plans as Plan[] | undefined
)

watch(
  monthlyPlans,
  () => {
    if (!monthlyPlans.value) return
    selectedPlan.value = monthlyPlans.value[0]
  },
  { immediate: true }
)

const lowestActivePlanItem = computed(() =>
  selectedPlan.value?.items.find(
    (item, index, arr) =>
      item.status === PlanItemStatus.Active || index === arr.length - 1
  )
)

const handleClick = () => {
  const yearlyPlans = plans.value?.groupByBillingCycle.find(
    (group) => group.billingCycleType === 'year'
  )

  const relevantPlans = [
    yearlyPlans?.plans.find((plan) => plan.name === selectedPlan.value?.name),
    selectedPlan.value,
  ]

  dialog.open(Dialogs.SelectPlanBilling, {
    props: { plans: relevantPlans },
    events: {
      success(plan: unknown) {
        selectedPlan.value = plan as Plan
        dialog.close()
        continueFlow()
      },
    },
  })
}

const handleFreeClick = () => {
  analytics.track('onboarding_completed', {
    trigger: 'basic_link',
    source: webviewStore.isActive
      ? webviewStore.isiOS
        ? 'apple'
        : 'google'
      : 'web',
  })

  if (webviewStore.isActive) {
    return webviewStore.postMessage({
      type: NativeActionsEnum.Basic,
    })
  }

  completeOnboarding()
  return router.push('/home')
}

const continueFlow = async () => {
  if (!selectedPlan.value || !lowestActivePlanItem.value) return

  if (webviewStore.isActive) {
    return webviewStore.postMessage({
      type: NativeActionsEnum.PlanSelected,
      data: {
        productId: lowestActivePlanItem.value.nativeProductId,
        planId: lowestActivePlanItem.value.nativePlanId,
      },
    })
  }

  if (
    ![SelectPlanType.Register, SelectPlanType.Onboarding].includes(
      props.type
    ) &&
    !billingStore.canUpdateSubscription
  ) {
    return dialog.open(Dialogs.IAP, { props: { type: 'change' } })
  }

  if (
    props.type === SelectPlanType.Change &&
    lowestActivePlanItem.value.productId === billingStore.billing?.plan.id &&
    selectedPlan.value.items.length === 1
  ) {
    return router.push('/settings/billing')
  }

  const isSubscribedToBundlePlan = selectedPlan.value.items.some(
    (item) => billingStore.billing?.plan.id === item.productId
  )

  if (
    props.type === SelectPlanType.Change &&
    isSubscribedToBundlePlan &&
    selectedPlan.value.items.length > 1
  ) {
    return dialog.open(Dialogs.UpgradeBundleLimit)
  }

  const billingCycleType = lowestActivePlanItem.value.prices.find(
    (price) => price.type === PlanPriceType.Subscription
  )?.details?.billingCycleType

  billingStore.setPlanData({
    plan: selectedPlan.value,
    planItem: lowestActivePlanItem.value,
    billingCycle: {
      type: billingCycleType || 'month',
      description:
        plans.value?.groupByBillingCycle.find(
          (billingCycle) => billingCycle?.billingCycleType === billingCycleType
        )?.billingCycleDescription || '',
    },
  })

  if (
    props.type === SelectPlanType.Register ||
    props.type === SelectPlanType.Onboarding
  ) {
    userStore.updateUser({ totalFiles: 0 })
  }

  if (props.type === SelectPlanType.Change) {
    return router.push('/change-plans/confirm')
  }

  router.push(`/${props.type.toLowerCase()}/checkout`)
}
</script>
